import { createFileRoute } from '@tanstack/react-router';
import { Alert, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { PlatformRole, hasRole, useSession } from '../auth/session';
import { DesignerScreen } from '../features/designer/DesignerScreen';
import { BackLink } from '../components/BackLink';

/**
 * A sibling of the tenant detail page rather than a child of it.
 *
 * The trailing underscore keeps this route parented at the root: the designer needs the full window
 * width for palette, canvas, inspector and preview, and nesting it inside the tenant page's tab
 * strip would spend a third of the screen on chrome the operator does not need while designing.
 */
export const Route = createFileRoute('/tenants_/$tenantId_/designer')({
  validateSearch: (search: Record<string, unknown>): { templateId?: string } => ({
    templateId: typeof search.templateId === 'string' ? search.templateId : undefined,
  }),
  component: DesignerPage,
});

/**
 * The editor's height: everything the AppShell leaves us. Derived from the shell's own CSS
 * variables so a header or padding change cannot strand a stale magic number here (this used to be
 * a hardcoded `calc(100vh - 96px)`).
 */
const editorHeight =
  'calc(100dvh - var(--app-shell-header-offset, 0rem) - 2 * var(--app-shell-padding, 0rem))';

function DesignerPage() {
  const { t } = useTranslation('tenants');
  const { tenantId } = Route.useParams();
  const { templateId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: session } = useSession();

  // Designer is the lowest role that may edit a template at all.
  if (!hasRole(session, PlatformRole.Designer, tenantId)) {
    return (
      <Stack gap="md" maw={560}>
        <BackLink to="/tenants/$tenantId" params={{ tenantId }} search={{ tab: 'signatures' }} />
        <Alert color="red" title={t('noAccessDesignerTitle')}>
          {t('noAccessDesignerBody')}
        </Alert>
      </Stack>
    );
  }

  // Picker mode scrolls like a normal page; editor mode is locked to the viewport so the three
  // panes manage their own scrolling.
  return (
    <Stack gap="sm" style={templateId ? { height: editorHeight } : undefined}>
      <BackLink to="/tenants/$tenantId" params={{ tenantId }} search={{ tab: 'signatures' }} />
      <DesignerScreen
        tenantId={tenantId}
        templateId={templateId ?? null}
        onSelectTemplate={(id) => void navigate({ search: { templateId: id } })}
      />
    </Stack>
  );
}
